const CHANNEL_NAME = 'inactivity-logout';
const LAST_ACTIVITY_KEY = 'inactivity:lastActivity';
const ACTIVITY_THROTTLE_MS = 1000;

export type CrossTabMessage =
  | { type: 'activity'; at: number }
  | { type: 'stayLoggedIn' }
  | { type: 'logout' };

export const createInactivityCrossTabSync = () => {
  const channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  let lastNotifyAt = 0;

  const post = (msg: CrossTabMessage) => {
    channel?.postMessage(msg);
  };

  const writeLastActivity = (at: number) => {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
    } catch {
      // Storage writes can fail in private mode or when quota is exhausted;
      // cross-tab sync degrades gracefully without the shared timestamp.
    }
  };

  const notifyActivity = () => {
    const now = Date.now();
    if (now - lastNotifyAt < ACTIVITY_THROTTLE_MS) return;
    lastNotifyAt = now;
    writeLastActivity(now);
    post({ type: 'activity', at: now });
  };

  const notifyStayLoggedIn = () => {
    const now = Date.now();
    lastNotifyAt = now;
    writeLastActivity(now);
    post({ type: 'stayLoggedIn' });
  };

  const notifyLogout = () => {
    post({ type: 'logout' });
  };

  const getLastActivity = (): number | null => {
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  };

  const subscribe = (handler: (msg: CrossTabMessage) => void) => {
    if (!channel) return () => {};
    const listener = (e: MessageEvent<CrossTabMessage>) => handler(e.data);
    channel.addEventListener('message', listener);
    return () => channel.removeEventListener('message', listener);
  };

  const close = () => {
    channel?.close();
  };

  return {
    notifyActivity,
    notifyStayLoggedIn,
    notifyLogout,
    getLastActivity,
    subscribe,
    close,
  };
};

export type InactivityCrossTabSync = ReturnType<typeof createInactivityCrossTabSync>;
